import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useBranding, resolveAsset } from "@/lib/branding";
import logoGoodBox from "@/assets/logo-goodbox-white.png";

export default function SetPassword() {
  const navigate = useNavigate();
  const { branding } = useBranding();
  const brandName = branding?.brand_name || "The Good Box";
  const params = new URLSearchParams(window.location.search);
  const token = params.get("token") || "";
  const kind = params.get("kind") || "invite";

  const [info, setInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [invalid, setInvalid] = useState("");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setInvalid("This link is missing its token."); setLoadingInfo(false); return; }
    api.get(`/auth/token-info?token=${encodeURIComponent(token)}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setInvalid(formatApiErrorDetail(e.response?.data?.detail) || "This link is invalid or has expired."))
      .finally(() => setLoadingInfo(false));
  }, [token]);

  const isInvite = (info?.kind || kind) === "invite";
  const heading = isInvite ? "Activate your account" : "Reset your password";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) return setError("Password must be at least 8 characters.");
    if (pw !== confirm) return setError("Passwords do not match.");
    setSaving(true);
    try {
      await api.post("/auth/set-password", { token, password: pw });
      setDone(true);
      setTimeout(() => navigate("/admin/login"), 2200);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not set your password.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-950">
      <div className="hidden lg:flex flex-col justify-between w-1/2 p-12 text-white relative overflow-hidden" style={{ background: "linear-gradient(135deg,#1e3a8a,#0f172a)" }}>
        <div className="flex items-center">
          <img src={branding?.logo_url ? resolveAsset(branding.logo_url) : logoGoodBox} alt={brandName} className="w-auto" style={{ height: "calc(28px * var(--brand-logo-scale, 1))", maxHeight: "56px" }} />
        </div>
        <div>
          <h1 className="font-admin-head text-4xl font-extrabold leading-tight">Welcome to<br />{brandName}.</h1>
          <p className="mt-4 text-slate-300 max-w-md">Set a secure password to access the administration portal for your estate.</p>
        </div>
        <p className="text-slate-500 text-sm">{brandName} · vend.thegoodbox.com</p>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm" data-testid="set-password-card">
          {loadingInfo ? (
            <div className="flex items-center gap-2 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /> Checking your link…</div>
          ) : invalid ? (
            <div data-testid="set-password-invalid" className="text-center">
              <ShieldAlert className="h-10 w-10 text-rose-400 mx-auto" />
              <h2 className="font-admin-head text-2xl font-bold text-white mt-4">Link unavailable</h2>
              <p className="text-slate-400 text-sm mt-2">{invalid}</p>
              <Button className="mt-6 bg-blue-600 hover:bg-blue-700" onClick={() => navigate("/admin/login")}>Back to sign in</Button>
            </div>
          ) : done ? (
            <div data-testid="set-password-success" className="text-center">
              <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto" />
              <h2 className="font-admin-head text-2xl font-bold text-white mt-4">All set!</h2>
              <p className="text-slate-400 text-sm mt-2">Your password has been saved. Redirecting you to sign in…</p>
            </div>
          ) : (
            <>
              <h2 className="font-admin-head text-2xl font-bold text-white">{heading}</h2>
              <p className="text-slate-400 text-sm mt-1">for <b className="text-slate-200">{info?.email}</b></p>
              <form onSubmit={submit} className="mt-8 space-y-4">
                <div>
                  <Label className="text-slate-300">New password</Label>
                  <Input data-testid="set-password-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 8 characters" className="mt-1.5 bg-slate-900 border-slate-700 text-white" />
                </div>
                <div>
                  <Label className="text-slate-300">Confirm password</Label>
                  <Input data-testid="set-password-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5 bg-slate-900 border-slate-700 text-white" />
                </div>
                {error && <p data-testid="set-password-error" className="text-sm text-red-400">{error}</p>}
                <Button data-testid="set-password-submit" type="submit" disabled={saving} className="w-full h-11 bg-blue-600 hover:bg-blue-700">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (isInvite ? "Activate account" : "Reset password")}
                </Button>
              </form>
              {info?.email?.endsWith("@goodnus.com") && (
                <p className="text-xs text-slate-500 mt-4">Tip: once activated you can also sign in with your goodnus.com Google account.</p>
              )}
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
